import styled from '@emotion/styled';
import { MuiThemeProvider } from '@material-ui/core';
import { History } from 'history';
import { ClassAttributes, Component, createElement } from 'react';
import { IntlProvider } from 'react-intl';
import { Router } from 'react-router';
import { ScrollContext } from 'react-router-scroll-4';

import Config from '../core/Config';
import Dependency from '../core/Dependency';
import State from '../core/State';
import GlobalStyle from '../GlobalStyle';
import theme from '../theme';
import { HEADBAR_HEIGHT } from './component/compound/HeadBar';
import ModalSpinner from './component/compound/ModalSpinner';
import _SigningInIndicatorScreen from './component/compound/SigningInIndicatorScreen';
import DocumentTitle from './connected/DocumentTitle';
import _FatalErrorScreen from './connected/FatalErrorScreen';
import _FormattedSnackbar from './connected/FormattedSnackbar';
import _HeadBar from './connected/HeadBar';
import MenuDrawer from './connected/MenuDrawer';
import _NonFatalErrorSnackbars from './connected/NonFatalErrorSnackbars';
import _SignedInScreen from './connected/SignedInScreenConnected';
import _SigningInScreen from './connected/SigningInScreenConnected';
import { COLOR_PAGE_BACKGROUND } from './constant/color';
import { Provider as ConfigProvider } from './context/config';
import { Provider as DependencyProvider } from './context/dependency';
import { Provider as HistoryProvider } from './context/history';
import { Provider as StateProvider } from './context/state';
import { flexibleLayoutContainer } from './styleUtility/flexibleLayout';
// import { jss, JssProvider } from '../customJss';

interface Props {
  config: Config;
  dependency: Dependency;
  history: History;
  state: State;
}

class Root extends Component<ClassAttributes<HTMLElement> & Props, Record<string, unknown>> {
  componentDidMount() {
    this.props.dependency.authenticationUsecase.signInWithPreviousSession();
  }

  componentDidUpdate() {
    this.props.dependency.authenticationUsecase.redirect();
  }

  componentDidCatch(err: Error) {
    this.props.dependency.errorReporter.reportError(err);
    this.props.dependency.errorPresenter.showAsFatal(err);
  }

  render() {
    return (
      <ConfigProvider value={this.props.config}>
        <DependencyProvider value={this.props.dependency}>
          <HistoryProvider value={this.props.history}>
            <StateProvider value={this.props.state}>
              <GlobalStyle />

              <Router history={this.props.history}>
                {/* <JssProvider jss={jss}> */}
                <MuiThemeProvider theme={theme}>
                  <IntlProvider locale={this.props.state.language} messages={this.props.state.translations}>
                    <ScrollContext>
                      <RootElement
                        noHeadBar={!this.props.state.authentication.signedInUser || !!this.props.state.fatalError}
                      >
                        <DocumentTitle />

                        <SigningInIndicatorScreen visible={this.props.state.authentication.fetching >= 1} />

                        <SomethingProcessing
                          visible={this.props.state.fullScreenSpinner.necessity >= 1}
                          loadingMessage={this.props.state.fullScreenSpinner.loadingMessage}
                        />

                        <NonFatalErrorSnackbars />

                        <FormattedSnackbar />

                        <HeadBar />
                        <Content>
                          <MenuDrawer />

                          <FatalErrorScreen />
                          <SigningInScreen />
                          <SignedInScreen />
                        </Content>
                      </RootElement>
                    </ScrollContext>
                  </IntlProvider>
                </MuiThemeProvider>
                {/* </JssProvider> */}
              </Router>
            </StateProvider>
          </HistoryProvider>
        </DependencyProvider>
      </ConfigProvider>
    );
  }
}

const RootElement = styled('div')<{ noHeadBar: boolean }>(props => ({
  paddingTop: props.noHeadBar ? 0 : HEADBAR_HEIGHT,
  backgroundColor: COLOR_PAGE_BACKGROUND,
}));

const HeadBar = styled(_HeadBar)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: HEADBAR_HEIGHT,
});

const FatalErrorScreen = styled(_FatalErrorScreen)({
  width: '100%',
  height: '100%',
});

const SigningInScreen = styled(_SigningInScreen)({
  width: '100%',
  height: '100%',
  maxWidth: '1180px',
  margin: '0 auto',
});

const SignedInScreen = styled(_SignedInScreen)({
  width: '100%',
  height: '100%',
  maxWidth: '1180px',
  margin: '0 auto',
});

const NonFatalErrorSnackbars = styled(_NonFatalErrorSnackbars)({
  zIndex: theme.zIndex.snackbar + 1,
});

const FormattedSnackbar = styled(_FormattedSnackbar)({
  zIndex: theme.zIndex.snackbar,
});

const SomethingProcessing = styled(ModalSpinner)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000 + 1,
});

const SigningInIndicatorScreen = styled(_SigningInIndicatorScreen)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000,
});

const Content = styled('div')(flexibleLayoutContainer({}));

export default Root;
